import { ContactsView } from "@/components/contact/ContactsView";
import CopyToClipboardView from "@/components/views/CopyToClipboardView";
import HtmlView from "@/components/views/HtmlView";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { formatAddress, formatIban, formatLocaleDate, formatPhoneNumber } from "@/utils/formatUtils";
import { tBoolean } from "@/utils/translationUtils";
import { useQueryClient } from "@tanstack/react-query";
import { Card, Col, Descriptions, Flex, Row, Tag } from "antd";
import { DescriptionsItemType } from "antd/es/descriptions";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import { useGetAgentEnumerations, USER_QUERY_KEYS } from "../../../../../common/api/queries";
import { rowGutter } from "../../../../../common/constants";
import { AgentProfilePictureEditor } from "../../../../agent/components/AgentProfilePictureEditor";
import { PersonalData } from "../../../../agent/components/dataview/PersonalData";
import AgentActiveTag from "../../../../agent/components/tags/AgentActiveTag";
import AgentCanBeGainerTag from "../../../../agent/components/tags/AgentCanBeGainerTag";
import { AgentTipperTag } from "../../../../agent/components/tags/AgentTipperTag";
import { AgentType } from "../../../../agent/enums";
import { AGENT_ROUTE_PATHS } from "../../../../agent/paths";
import { Agent } from "../../../../agent/types";
import {
  formatAgentDirectorateName,
  formatAgentName,
  formatAgentSystemIdNumber,
  isAgentTipper,
  isLegalAgent,
  isNaturalAgent,
  isSelfEmployedAgent,
} from "../../../../agent/utils";
import { useCurrentAgentAccount, useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { refreshSession } from "../../../../auth/session";
import { AgentUserProfile, ClientUserProfile } from "../../../types";
import { isAgentUserProfile } from "../../../utils";

const DESCRIPTION_LABEL_WIDTH = 200;
const VIEW_TYPE: "narrow" | "wide" = "narrow";

interface Props {
  agent?: Agent;
}

export const UserProfileUserAccountView = ({ agent }: Props) => {
  const { t } = useTranslation();

  const userAccount = useCurrentAgentAccount();
  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const { data } = useGetAgentEnumerations(agent?.parent?.id ? [agent.parent?.id] : []);
  const agentParentEnum = data?.[0];

  const queryClient = useQueryClient();

  const items = useMemo(() => {
    const items: DescriptionsItemType[] = [];

    if (!agent) {
      return items;
    }

    if (!isNaturalAgent(agent)) {
      items.push({
        label: t("agent.attrs.companyName"),
        children: agent.companyName,
      });
    } else {
      items.push({
        label: t("agent.attrs.aggregatedName"),
        children: agent.aggregatedName,
      });
    }

    items.push({
      label: t("agent.titles.structuralData"),
      children: (
        <Descriptions
          size="small"
          bordered
          column={1}
          styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
          items={[
            {
              label: t("agent.attrs.systemIdNumber"),
              children: <CopyToClipboardView content={formatAgentSystemIdNumber(agent)} />,
            },
            {
              label: t("agent.attrs.partnerIdNumber"),
              children: <CopyToClipboardView content={agent.partnerIdNumber} />,
            },
            {
              label: t("agent.attrs.directorate"),
              children: agent.directorate ? formatAgentDirectorateName(agent.directorate) : undefined,
            },
            {
              label: t("agent.attrs.parentId"),
              children: (
                <Flex justify="space-between">
                  <div>{formatAgentName(agent.parent)}</div>
                </Flex>
              ),
            },
            agentParentEnum?.representatives?.length && {
              label: t("agent.helpers.parentRepresentatives"),
              children: agentParentEnum.representatives
                .map((representative) => formatAgentName(representative))
                .join(", "),
            },
          ].filter((item) => !!item)}
        />
      ),
    });

    if (isNaturalAgent(agent)) {
      items.push({
        label: t("agent.titles.personalData"),
        children: <PersonalData agent={agent} width={DESCRIPTION_LABEL_WIDTH} />,
      });

      items.push({
        label: t("agent.helpers.representativeFor"),
        children: agent.representedAgents.map((agent, index) => (
          <Fragment key={agent.id}>
            {index > 0 && ", "}
            <Link to={generatePath(AGENT_ROUTE_PATHS.detail.to, { id: agent.id })} target="_blank">
              {formatAgentName(agent)}
            </Link>
          </Fragment>
        )),
      });
    } else if (isSelfEmployedAgent(agent)) {
      items.push({
        label: t("agent.titles.personalData"),
        children: <PersonalData agent={agent} width={DESCRIPTION_LABEL_WIDTH} />,
      });

      items.push({
        label: t("agent.titles.companyData"),
        children: (
          <Descriptions
            size="small"
            bordered
            styles={{ label: { width: 170 } }}
            column={2}
            items={[
              {
                label: t("agent.attrs.companyRegistrationNumber"),
                children: <CopyToClipboardView content={agent.companyRegistrationNumber} />,
              },
              {
                label: t("agent.attrs.districtOffice"),
                children: agent.districtOffice,
              },
              {
                label: t("agent.attrs.taxId"),
                children: <CopyToClipboardView content={agent.taxId} />,
              },
              {
                label: t("agent.attrs.tradeRegisterNumber"),
                children: agent.tradeRegisterNumber,
              },
              {
                label: t("agent.attrs.vatId"),
                children: <CopyToClipboardView content={agent.vatId} />,
              },
            ]}
          />
        ),
      });
    } else if (isLegalAgent(agent)) {
      items.push({
        label: t("agent.titles.companyData"),
        children: (
          <Descriptions
            size="small"
            bordered
            styles={{ label: { width: 170 } }}
            layout={VIEW_TYPE === "narrow" ? "vertical" : "horizontal"}
            column={2}
            items={[
              {
                label: t("agent.attrs.companyRegistrationNumber"),
                children: <CopyToClipboardView content={agent.companyRegistrationNumber} />,
              },
              {
                label: t("agent.attrs.commercialRegisterDistrictOffice"),
                children: agent.commercialRegisterDistrictOffice,
              },
              {
                label: t("agent.attrs.taxId"),
                children: <CopyToClipboardView content={agent.taxId} />,
              },
              {
                label: t("agent.attrs.commercialRegisterSection"),
                children: agent.commercialRegisterSection,
              },
              {
                label: t("agent.attrs.vatId"),
                children: <CopyToClipboardView content={agent.vatId} />,
              },
              {
                label: t("agent.attrs.commercialRegisterInsertionNumber"),
                children: agent.commercialRegisterInsertionNumber,
              },
              {
                label: t("agent.attrs.legalTypeChangeDate"),
                children: agent.legalTypeChangeDate ? (
                  formatLocaleDate(agent.legalTypeChangeDate)
                ) : (
                  <span className="sub-header-info">---</span>
                ),
              },
            ]}
          />
        ),
      });

      items.push({
        label: t("agent.attrs.representatives"),
        children: agent.representatives.map((agent, index) => (
          <Fragment key={agent.id}>
            {index > 0 && ", "}
            <Link to={generatePath(AGENT_ROUTE_PATHS.detail.to, { id: agent.id })} target="_blank">
              {formatAgentName(agent)}
            </Link>
          </Fragment>
        )),
      });
    }

    items.push({
      label: t("agent.titles.scope"),
      children: (
        <Descriptions
          size="small"
          bordered
          column={1}
          styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
          items={[
            {
              label: "Zmluvné vzťahy",
              children: (
                <div>
                  {agent.status && (
                    <div>
                      <Descriptions
                        size="small"
                        bordered
                        styles={{ label: { width: 140 } }}
                        layout={VIEW_TYPE === "narrow" ? "vertical" : "horizontal"}
                        column={1}
                        items={[
                          {
                            label: isTopAgentPlus
                              ? t("agent.helpers.agentStatusSFAPlusShortLabel")
                              : t("agent.enums.agentStatus._label"),
                            children: agent.status ? t(`agent.enums.agentStatus.${agent.status}`) : undefined,
                          },
                          {
                            label: t("common.date"),
                            children: `${agent.statusStartDate ? `${t("common.from")} ${formatLocaleDate(agent.statusStartDate)}` : ""} ${agent.statusStartDate && agent.statusEndDate ? "-" : ""} ${agent.statusEndDate ? `${t("common.to")} ${formatLocaleDate(agent.statusEndDate)}` : ""}`,
                          },
                        ]}
                      />
                    </div>
                  )}

                  {isTopAgentPlus && (
                    <div>
                      <Descriptions
                        className="margin-top-small"
                        size="small"
                        bordered
                        column={1}
                        styles={{ label: { width: 140 } }}
                        layout={VIEW_TYPE === "narrow" ? "vertical" : "horizontal"}
                        items={[
                          {
                            label: t("commissions.batch.enums.commissionBatchType_PLUS.SECONDARY"),
                            children: agent.secondaryStatus
                              ? t(`agent.enums.agentSecondaryStatus.${agent.secondaryStatus}`)
                              : undefined,
                          },
                          {
                            label: t("common.date"),
                            children: `${agent.secondaryStatusStartDate ? `${t("common.from")} ${formatLocaleDate(agent.secondaryStatusStartDate)}` : ""} ${agent.secondaryStatusStartDate && agent.secondaryStatusEndDate ? "-" : ""} ${agent.secondaryStatusEndDate ? `${t("common.to")} ${formatLocaleDate(agent.secondaryStatusEndDate)}` : ""}`,
                          },
                        ]}
                      />
                    </div>
                  )}
                </div>
              ),
            },
            {
              label: t("agent.attrs.realtySpecialist"),
              children: tBoolean(agent.realtySpecialist),
            },
            {
              label: t("agent.attrs.loanSpecialist"),
              children: tBoolean(agent.loanSpecialist),
            },
            {
              label: t("common.financialSectors"),
              children: agent.sectors.map((sector) => t("agent.enums.financialSector." + sector)).join(", "),
            },
          ]}
        />
      ),
    });

    items.push({
      label: t("agent.titles.basicData"),
      children: (
        <Descriptions
          size="small"
          bordered
          column={1}
          styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
          items={[
            {
              label: t("agent.attrs.email"),
              children: (
                <CopyToClipboardView
                  content={agent.email}
                  wrapper={<a href={`mailto:${agent.email}`}>{agent.email}</a>}
                />
              ),
            },
            {
              label: t("agent.attrs.phone"),
              children: (
                <CopyToClipboardView
                  content={agent.phone}
                  wrapper={<a href={`tel:${agent.phone}`}>{formatPhoneNumber(agent.phone)}</a>}
                />
              ),
            },
            {
              label: t("agent.attrs.web"),
              children: agent.web,
            },
            {
              label: t("agent.attrs.bankAccountNumber"),
              children: (
                <CopyToClipboardView
                  content={agent.bankAccountNumber ? formatIban(agent.bankAccountNumber) : undefined}
                />
              ),
            },
            {
              label: t("agent.attrs.nbsRegistrationNumber"),
              children: <CopyToClipboardView content={agent.nbsRegistrationNumber} />,
            },
            {
              label: t("agent.attrs.note"),
              children: agent.note ? <HtmlView value={agent.note} /> : undefined,
            },
          ]}
        />
      ),
    });

    items.push({
      label: t("agent.titles.addresses"),
      children: (
        <Descriptions
          size="small"
          bordered
          column={1}
          styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
          items={[
            {
              label: agent.type === AgentType.LEGAL ? t("agent.attrs.addressLongPO") : t("agent.attrs.addressLong"),
              children: formatAddress(agent.address),
            },
            {
              label:
                agent.type === AgentType.SELF_EMPLOYED
                  ? t("agent.attrs.serviceAddressFOP")
                  : t("agent.attrs.serviceAddress"),
              children: agent.serviceAddress ? formatAddress(agent.serviceAddress) : undefined,
            },
            {
              label: t("agent.attrs.correspondenceAddress"),
              children: agent.correspondenceAddress ? formatAddress(agent.correspondenceAddress) : undefined,
            },
          ]}
        />
      ),
    });

    items.push({
      label: t("agent.titles.contacts"),
      children: <ContactsView contacts={agent.contacts} />,
    });

    return items;
  }, [agent, agentParentEnum, isTopAgentPlus, VIEW_TYPE]);

  if (!agent) {
    return null;
  }

  return (
    <>
      <ItemCreatedUpdatedInfoView item={agent} className="margin-bottom-tiny" />

      <div className="margin-top-medium">
        {isAgentTipper(agent) ? <AgentTipperTag /> : undefined}
        <AgentCanBeGainerTag canBeGainer={agent.canBeGainer} />
        <AgentActiveTag agentDeactivated={agent.deactivated} />
        {(isLegalAgent(agent) || isSelfEmployedAgent(agent)) && agent.vatId && (
          <Tag color="blue">{t("agent.helpers.vatPayer")}</Tag>
        )}
      </div>

      <Row className="margin-top-tiny" gutter={rowGutter}>
        <Col span={18}>
          <Descriptions styles={{ label: { fontWeight: "bold", minWidth: 180 } }} column={1} bordered items={items} />
        </Col>

        <Col span={6}>
          <Card size="small">
            <Flex justify="center">
              <AgentProfilePictureEditor
                agent={agent}
                width={185}
                showActions
                updateProfilePictureCallback={(profilePicture) => {
                  queryClient.setQueriesData<AgentUserProfile | ClientUserProfile>(
                    { queryKey: USER_QUERY_KEYS.userGetUserProfile() },
                    (userProfile) => {
                      if (!userProfile || !isAgentUserProfile(userProfile)) {
                        return userProfile;
                      }

                      return {
                        ...userProfile,
                        agent:
                          userProfile.agent.id === agent.id
                            ? { ...userProfile.agent, profilePicture: profilePicture }
                            : userProfile.agent,
                        representingAgent:
                          userProfile.representingAgent?.id === agent.id
                            ? { ...userProfile.representingAgent, profilePicture: profilePicture }
                            : userProfile.representingAgent,
                      };
                    },
                  );

                  // The header shows the current agent's profile picture.
                  if (userAccount?.agent.id === agent.id || userAccount?.representingAgent?.id === agent.id) {
                    refreshSession();
                  }
                }}
              />
            </Flex>
          </Card>
        </Col>
      </Row>
    </>
  );
};
